import { useQuery } from '@apollo/client'
import type { ReactNode } from 'react'
import { useEffect } from 'react'

import { MY_DETAILS } from '../data/queries/user'
import { useSelectedWorkspaceStore } from '../stores/auth-store'
import type { AnalyticsContextProps } from '../types/analytics'
import type { MeReturnType } from '../types/user'
import { captureEvent, capturePageview, identify, identifyWorkspace, setUserProperty } from '../utils/analytics-service'
import { tokenUtils } from '../utils/token-utils'
import { createGenericContext } from './generic-context'

const [useAnalytics, AnalyticsContextProvider] = createGenericContext<AnalyticsContextProps>()

interface AnalyticsProviderProps {
  children: ReactNode
}

const AnalyticsProvider = ({ children }: AnalyticsProviderProps) => {
  const userID = tokenUtils.getUserID() || undefined
  const { selectedWorkspace } = useSelectedWorkspaceStore()

  const { data } = useQuery<{ me: MeReturnType }>(MY_DETAILS, {
    fetchPolicy: 'cache-first',
    skip: !userID
  })

  useEffect(() => {
    //Identify the user
    if (userID && typeof window !== 'undefined') {
      identify(userID)
    }
  }, [userID])

  useEffect(() => {
    //Set the user properties for the user
    if (!data?.me || typeof window === 'undefined') return

    const { name, email, phone } = data.me
    setUserProperty({
      user_email: email || '',
      user_name: name || '',
      user_phone: phone || ''
    })
  }, [data?.me])

  useEffect(() => {
    //Set the workspace for the user
    if (!selectedWorkspace?.workspace || typeof window === 'undefined') return

    const { id, displayName, workspacename, isVerified } = selectedWorkspace.workspace
    identifyWorkspace({ displayName, id, isVerified: isVerified ?? false, workspacename })
  }, [selectedWorkspace?.workspace])

  const value: AnalyticsContextProps = {
    captureEvent: typeof window === 'undefined' ? () => {} : captureEvent,
    capturePageview: typeof window === 'undefined' ? () => {} : capturePageview,
    identify: typeof window === 'undefined' ? () => {} : identify,
    identifyWorkspace: typeof window === 'undefined' ? () => {} : identifyWorkspace,
    setUserProperty: typeof window === 'undefined' ? () => {} : setUserProperty
  }

  return <AnalyticsContextProvider value={value}>{children}</AnalyticsContextProvider>
}

export { AnalyticsProvider, useAnalytics }
